<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <QTable
                    :data="result"
                    :columns="formattedColumns"
                    :hide-bottom="true"
                    :rows-per-page-options="[0]"
                    :loading="isLoading"
                    class="q-table"
                >
                    <template
                        v-slot:bottom-row
                        v-if="result.length == 0 && !isError && !isLoading"
                    >
                        <q-tr>
                            <q-td v-if="isInitial" colspan="12"
                                >Please select one or more filters to generate
                                this report.
                            </q-td>
                            <q-td v-else colspan="12"
                                >No records available</q-td
                            >
                        </q-tr>
                    </template>
                    <template v-slot:header-cell="{ col }">
                        <th>
                            <strong>{{ col.label }}</strong>
                        </th>
                    </template>
                    <template v-slot:body-cell-client_name="props">
                        <q-td :props="props" class="">
                            <client-name
                                :use-router="USE_CLIENT_MODULE_VUE"
                                :client="props.row.client"
                            />
                        </q-td>
                    </template>
                    <template v-slot:body-cell-patient_name="props">
                        <q-td :props="props" class="">
                            <patient-name
                                :use-router="USE_PATIENT_MODULE_VUE"
                                :patient="props.row.patient"
                                :show-status="false"
                            />
                        </q-td>
                    </template>
                    <template v-slot:body-cell-Item="props">
                        <q-td :props="props">
                            <item-name
                                :item="{
                                    name: props.row.Item,
                                    encodedId: props.row.item_id
                                }"
                            />
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import { QTable } from "quasar";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import ClientName from "@/components/commons/client-name";
import PatientName from "@/components/commons/patient-name";
import { forEach } from "lodash";
import ItemName from "@/components/commons/item-name";
const REPORT_NAME = "Unique Dispensing Log";
export default {
    components: {
        QTable,
        ClientName,
        PatientName,
        ItemName,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getEndDate("day")
                };
            }
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            isLoading: false,
            isInitial: true,
            result: [],
            title: REPORT_NAME,
            isError: false,
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true",
            dateRange: "",
            formattedColumns: [
                {
                    name: "dispensed",
                    label: "Dispensed",
                    field: "dispensed",
                    align: "left",
                    style: "width: 10%;"
                },
                {
                    name: "patient_name",
                    label: "Patient (ID)",
                    field: "patient_name",
                    align: "left",
                    style: "width: 15%;",
                    classes: "text-capitalize"
                },
                {
                    name: "client_name",
                    label: "Client (ID)",
                    field: "client_name",
                    align: "left",
                    style: "width: 15%;",
                    classes: "text-capitalize"
                },
                {
                    name: "given_by",
                    label: "Given By",
                    field: "given_by",
                    align: "left",
                    style: "width: 10%;",
                    classes: "text-capitalize"
                },
                {
                    name: "Item",
                    label: "Item",
                    field: "Item",
                    align: "left",
                    style: "width: 13%;",
                    classes: "text-capitalize"
                },
                {
                    name: "quantity",
                    label: "Qty",
                    field: "quantity",
                    align: "left",
                    style: "width: 10%;"
                },
                {
                    name: "route",
                    label: "Route",
                    field: "route",
                    align: "left",
                    style: "width: 12%;",
                    classes: "text-capitalize"
                },
                {
                    name: "instruction",
                    label: "Instructions",
                    field: "instruction",
                    align: "left",
                    style: "width: 15%;"
                }
            ]
        };
    },
    async mounted() {
        let self = this;
        await self._getSetting();
        self.data.isShowDateRange = true;
        self.data.datePickerOptionDefault = 1;
        self.data.reportId = CREPORT.TYPE.UNIQUE_DISPENSING_LOG; // tells filter dialog that 95 is using you
        self.data.itemType = 3;
        self.data.isShowItemOptions = true;
        self.data.isShowProviderOptions = true;
        self.data.filterTitle = "Filter Report";
        self.data.start = this.getStartDate("day");
        self.data.end = this.getEndDate("day");
        self.data.from = this.getStartDate("day").unix();
        self.data.to = this.getEndDate("day").unix();
        self.dynamicPageTitle(self.title);
        self.dateFormat = self.getDateFormat();

        self.dialogs["filterDateRangeWindow"].open(self.data);
        self.$root.$on("report-filter-date-range:refresh", params => {
            self.init(params);
        });
    },

    beforeDestroy: function () {
        this.data.itemType = null;
        this.data.datePickerOptionDefault = null;
        this.data.reportId = null;
        this.data.isShowItemOptions = false;
        this.data.isShowProviderOptions = false;
        this.data.filterTitle = "Filter Date";
        this.data.items = [];
        this.data.provider = [];
        this.data.itemShowMode = 1;
        this.data.providerShowMode = 1;

        let self = this;
        this.$root.$off("report-filter-date-range:refresh", params => {
            self.init(params);
        });
    },
    methods: {
        async init(params) {
            let self = this;

            let uniqueDispensingItemData = [];
            let uniqueDispensingProviderData = [];
            self.isLoading = true;
            self.isError = false;
            self.result = [];

            if (params) {
                if (
                    params.itemShowMode === 2 &&
                    params.items &&
                    params.items.length > 0
                ) {
                    uniqueDispensingItemData = params.items.map(
                        item => item.raw_id
                    );
                    self.data.items = params.items;
                } else {
                    self.data.items = [];
                }

                if (
                    params.providerShowMode === 2 &&
                    params.provider &&
                    params.provider.length > 0
                ) {
                    uniqueDispensingProviderData = params.provider.map(
                        provider => provider.value
                    );
                }
            }
            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
            }
            self.dateRange = self.getDateRange(self.data.from, self.data.to);

            await ReportService.getReport(CREPORT.TYPE.UNIQUE_DISPENSING_LOG, {
                from: self.data.from,
                to: self.data.to,
                uniqueDispensingItemData: uniqueDispensingItemData,
                uniqueDispensingProviderData: uniqueDispensingProviderData
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        forEach(res.resource.results, function (result) {
                            result.patient_name = result["Patient (ID)"];
                            result.patient = self.generatePatientObject(result);
                            result.client_name = result["Client (ID)"];
                            result.client = self.generateClientObject(result);
                            result.given_by = result["Given By"];
                            self.result.push(result);
                        });

                        self.dateRange = self.getDateRange(
                            res.resource.meta.fromDate,
                            res.resource.meta.toDate
                        );
                        self.title = res.resource.meta.title;
                        self.dynamicPageTitle(self.title);
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self._message("Error: Unable to get the report", "danger");
                    self.isError = true;
                })
                .finally(() => {
                    self.isLoading = false;
                    self.isInitial = false;
                });
        },
        generateClientObject(obj) {
            return {
                id: obj.client_id,
                name: obj["Client (ID)"].replace("(deleted)", ""),
                encoded_id: !obj["Client (ID)"].includes("(deleted)")
                    ? obj.client_id
                    : null
            };
        },
        generatePatientObject(obj) {
            return {
                name: obj["Patient (ID)"].replace("(deleted)", ""),
                encodedId: !obj["Patient (ID)"].includes("(deleted)")
                    ? obj.patient_id
                    : null
            };
        }
    }
};
</script>
